import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  CircularProgress,
  Button,
  Avatar,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  useTheme,
  Typography,
  useMediaQuery,
  Pagination
} from "@mui/material";
import {
  Instagram,
  Facebook,
  YouTube,
  LocationOn,
  ChevronLeft,
  ChevronRight,
  Info
} from "@mui/icons-material";
import { useAppSelector } from "../store/hooks";
import { selectUser, selectIsLoggedIn } from "../store/userSlice";
import { useAuth } from "../AuthContext";
import API_BASE_URL from "../apiConfig";
import { PRIMARY_PINK, PRIMARY_PINK_DARK } from "../constants";
import MKButton from "../components/MKButton";
import MKBox from "../components/MKBox";
import EventDetailsModal from "../components/EventDetailsModal";
import SignInModal from "../components/auth/SignInModal";

function ClubLandingPage() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Add CSS keyframes for carousel animations
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .carousel-container {
        position: relative;
        width: 100%;
        height: 100%;
        overflow: hidden;
      }
      
      .carousel-image {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.8s ease-in-out, opacity 0.8s ease-in-out;
        transform: scale(1.05);
      }
      
      .carousel-image.current {
        z-index: 2;
        transform: translateX(0) scale(1.05);
        opacity: 1;
      }
      
      .carousel-image.next {
        z-index: 1;
        transform: translateX(100%) scale(1.05);
        opacity: 1;
      }
      
      .carousel-image.prev {
        z-index: 1;
        transform: translateX(-100%) scale(1.05);
        opacity: 1;
      }
      
      .carousel-image.sliding-next {
        transform: translateX(-100%) scale(1.05);
        opacity: 1;
      }
      
      .carousel-image.sliding-prev {
        transform: translateX(100%) scale(1.05);
        opacity: 1;
      }
      
      .carousel-image.sliding-in-next {
        transform: translateX(0) scale(1.05);
        opacity: 1;
      }
      
      .carousel-image.sliding-in-prev {
        transform: translateX(0) scale(1.05);
        opacity: 1;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [followLoading, setFollowLoading] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [error, setError] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [news, setNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [feed, setFeed] = useState([]);
  const [feedLoading, setFeedLoading] = useState(true);
  const [feedError, setFeedError] = useState("");
  const [feedPage, setFeedPage] = useState(1);
  const [feedPageSize] = useState(5); // Max 5 posts per page
  const [newPost, setNewPost] = useState("");
  const [replyBoxes, setReplyBoxes] = useState({}); // { postId: replyText }
  const [posting, setPosting] = useState(false);
  const [replying, setReplying] = useState({}); // { postId: boolean }
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [userCanPost, setUserCanPost] = useState(false);
  const [carouselPictures, setCarouselPictures] = useState([]);
  const [carouselLoading, setCarouselLoading] = useState(true);
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);
  const [carouselDirection, setCarouselDirection] = useState('next'); // 'next' or 'prev'
  const [carouselPaused, setCarouselPaused] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [emailSubscribed, setEmailSubscribed] = useState(false);
  const [emailSubLoading, setEmailSubLoading] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const { login } = useAuth();

  // Default avatar for clubs without one
  const defaultAvatar = "https://via.placeholder.com/150/1976d2/ffffff?text=CLUB";

  

  useEffect(() => {
    setLoading(true);
    setError(null);

    const fetchClub = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/clubs/${handle}`);

        if (!res.ok) {
          throw new Error(`Club not found: ${res.status}`);
        }

        const data = await res.json();
        setClub(data);
      } catch (err) {
        console.error('Error fetching club:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchClub();
  }, [handle]);

  // Fetch carousel pictures when club is loaded
  useEffect(() => {
    if (!club?.id || !club?.show_carousel) {
      setCarouselPictures([]);
      setCarouselLoading(false);
      return;
    }

    const fetchCarouselPictures = async () => {
      try {
        setCarouselLoading(true);
        const response = await fetch(`${API_BASE_URL}/api/clubs/${club.id}/carousel-pictures`);

        if (response.ok) {
          const data = await response.json();
          setCarouselPictures(data);
        } else {
          console.error('Failed to fetch carousel pictures');
          setCarouselPictures([]);
        }
      } catch (error) {
        console.error('Error fetching carousel pictures:', error);
        setCarouselPictures([]);
      } finally {
        setCarouselLoading(false);
      }
    };

    fetchCarouselPictures();
  }, [club?.id, club?.show_carousel]);

  // Separate effect for follow status and user permissions, runs after club and isLoggedIn are set
  useEffect(() => {
    if (!club || !isLoggedIn || !user || !user.id) {
      setIsFollowing(false);
      setUserCanPost(false);
      return;
    }
    const checkFollowing = async () => {
      try {
        const followRes = await fetch(`${API_BASE_URL}/api/clubs/${club.id}/following?user_id=${user.id}`);
        if (followRes.ok) {
          const followData = await followRes.json();
          setIsFollowing(followData.following);
        } else {
          setIsFollowing(false);
        }
      } catch (err) {
        setIsFollowing(false);
      }
    };
    checkFollowing();
    checkUserCanPost();
  }, [club, isLoggedIn, user]);

  // Fetch email subscription preference
  useEffect(() => {
    if (!club || !isLoggedIn || !user || !user.id) {
      setEmailSubscribed(false);
      return;
    }

    const fetchEmailPreference = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/clubs/${club.id}/email-preference`, {
          credentials: 'include'
        });
        if (res.ok) {
          const data = await res.json();
          setEmailSubscribed(data.email_notif_enabled);
        } else {
          setEmailSubscribed(false);
        }
      } catch (err) {
        console.error('Error fetching email preference:', err);
        setEmailSubscribed(false);
      }
    };

    fetchEmailPreference();
  }, [club, isLoggedIn, user]);

  // Fetch events for this club
  useEffect(() => {
    if (!club) return;
    setEventsLoading(true);
    fetch(`${API_BASE_URL}/api/clubs/${club.id}/events`)
      .then(res => res.json())
      .then(data => setEvents(Array.isArray(data) ? data : []))
      .catch(() => setEvents([]))
      .finally(() => setEventsLoading(false));
  }, [club]);

  // Fetch news for this club
  useEffect(() => {
    if (!club) return;
    setNewsLoading(true);
    fetch(`${API_BASE_URL}/api/clubs/${club.id}/news`)
      .then(res => res.json())
      .then(data => setNews(Array.isArray(data) ? data : []))
      .catch(() => setNews([]))
      .finally(() => setNewsLoading(false));
  }, [club]);

  // Fetch club feed
  useEffect(() => {
    if (!club) return;
    setFeedLoading(true);
    setFeedError("");
    fetch(`${API_BASE_URL}/api/clubs/${club.id}/feed`)
      .then(res => res.json())
      .then(data => setFeed(Array.isArray(data) ? data : []))
      .catch(() => setFeedError("Failed to load feed"))
      .finally(() => setFeedLoading(false));
  }, [club]);

  // Enhanced follow/unfollow with confirmation for unsubscribe
  const handleFollowToggle = async () => {
    if (!isLoggedIn) {
      // Show sign-in modal
      setShowSignInModal(true);
      return;
    }

    // If currently following, ask for confirmation before unfollowing
    if (isFollowing) {
      const confirmed = window.confirm(
        `Are you sure you want to unsubscribe from ${club.name}? You'll no longer receive updates from this club.`
      );
      if (!confirmed) return;
    }

    setFollowLoading(true);
    try {
      const method = isFollowing ? 'DELETE' : 'POST';
      const res = await fetch(`${API_BASE_URL}/api/clubs/${club.id}/follow`, {
        method,
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Failed to update follow status');

      setIsFollowing(!isFollowing);
      // Update follower count
      setClub(prev => ({
        ...prev,
        follower_count: isFollowing ? prev.follower_count - 1 : prev.follower_count + 1
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setFollowLoading(false);
    }
  };

  // Toggle email subscription
  const handleEmailSubscriptionToggle = async () => {
    if (!isLoggedIn) {
      // Show sign-in modal
      setShowSignInModal(true);
      return;
    }

    setEmailSubLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/clubs/${club.id}/email-preference`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ enabled: !emailSubscribed })
      });

      if (!res.ok) throw new Error('Failed to update email preference');

      const data = await res.json();
      setEmailSubscribed(data.email_notif_enabled);
    } catch (err) {
      console.error('Error updating email preference:', err);
      setError('Failed to update email notification preference');
    } finally {
      setEmailSubLoading(false);
    }
  };

  const handleSignIn = async (credentials) => {
    try {
      await login(credentials);
      setShowSignInModal(false);
    } catch (error) {
      console.error("Sign in error:", error);
      // Re-throw the error so SignInModal can handle it
      throw error;
    }
  };

  // Post a new top-level post
  const handlePost = async () => {
    if (!user || !newPost.trim() || !userCanPost) return;
    setPosting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/clubs/${club.id}/feed`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: user.id, content: newPost })
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || "Failed to post");
      }
      setNewPost("");
      // Add the new post with complete user data to the feed
      const data = await res.json();
      setFeed([{ ...data, replies: [] }, ...feed]);
    } catch (error) {
      console.error('Error posting:', error);
      // Optionally show error to user
    } finally {
      setPosting(false);
    }
  };

  // Open reply box for a post
  const openReplyBox = (postId) => {
    setReplyBoxes({ ...replyBoxes, [postId]: "" });
    setReplying({ ...replying, [postId]: false });
  };

  // Handle reply text change
  const handleReplyChange = (postId, value) => {
    setReplyBoxes({ ...replyBoxes, [postId]: value });
  };

  // Post a reply
  const handleReply = async (postId) => {
    if (!user || !replyBoxes[postId]?.trim() || !userCanPost) return;
    setReplying({ ...replying, [postId]: true });
    try {
      const res = await fetch(`${API_BASE_URL}/api/clubs/${club.id}/feed`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: user.id, content: replyBoxes[postId], parent_id: postId })
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || "Failed to reply");
      }
      const reply = await res.json();
      setFeed(feed => feed.map(post => post.id === postId ? { ...post, replies: [...post.replies, reply] } : post));
      setReplyBoxes({ ...replyBoxes, [postId]: "" });
    } catch (error) {
      console.error('Error replying:', error);
      // Optionally show error to user
    } finally {
      setReplying({ ...replying, [postId]: false });
    }
  };

  // Helper to format date
  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleString() : "";

  // Pagination helpers for feed
  const totalFeedPages = Math.ceil(feed.length / feedPageSize);
  const paginatedFeed = feed.slice((feedPage - 1) * feedPageSize, feedPage * feedPageSize);

  const handleFeedPageChange = (event, newPage) => {
    setFeedPage(newPage);
  };

  // Check if user can post to the club feed
  const checkUserCanPost = async () => {
    if (!club || !isLoggedIn || !user) {
      setUserCanPost(false);
      return;
    }

    // If club_mod_feed is false, anyone can post
    if (!club.club_mod_feed) {
      setUserCanPost(true);
      return;
    }

    // If club_mod_feed is true, check user permissions
    try {
      // Check if user is club owner
      const isOwner = user.id === club.owner_id;

      if (isOwner) {
        setUserCanPost(true);
        return;
      }

      // Check if user is club admin or mod by looking up their membership
      const res = await fetch(`${API_BASE_URL}/api/clubs/${club.id}/members`, {
        credentials: 'include'
      });

      if (res.ok) {
        const members = await res.json();
        const userMembership = members.find(member => member.user_id === user.id);

        if (userMembership && (
          userMembership.role_name === 'club admin' ||
          userMembership.role_name === 'club mod'
        )) {
          setUserCanPost(true);
          return;
        }
      }

      setUserCanPost(false);
    } catch (error) {
      console.error('Error checking user permissions:', error);
      setUserCanPost(false);
    }
  };

  // URL validation functions
  const isValidInstagramId = (id) => {
    return id && typeof id === 'string' && id.trim().length > 0;
  };

  const isValidFacebookUrl = (url) => {
    return url && typeof url === 'string' && (
      url.includes('facebook.com/') ||
      url.includes('www.facebook.com/') ||
      url.startsWith('https://facebook.com/') ||
      url.startsWith('https://www.facebook.com/')
    );
  };

  const isValidYouTubeUrl = (url) => {
    return url && typeof url === 'string' && (
      url.includes('youtube.com/') ||
      url.includes('www.youtube.com/') ||
      url.includes('youtu.be/') ||
      url.startsWith('https://youtube.com/') ||
      url.startsWith('https://www.youtube.com/') ||
      url.startsWith('https://youtu.be/')
    );
  };

  const isValidDiscordUrl = (url) => {
    return url && typeof url === 'string' && (
      url.includes('discord.gg/') ||
      url.includes('discord.com/invite/') ||
      url.startsWith('https://discord.gg/') ||
      url.startsWith('https://discord.com/invite/')
    );
  };

  const isValidMapsUrl = (url) => {
    return url && typeof url === 'string' && (
      url.includes('maps.google.com/') || 
      url.includes('www.google.com/maps/') ||
      url.includes('goo.gl/maps/') ||
      url.includes('maps.app.goo.gl/') ||
      url.startsWith('https://maps.google.com/') || 
      url.startsWith('https://www.google.com/maps/') ||
      url.startsWith('https://goo.gl/maps/') ||
      url.startsWith('https://maps.app.goo.gl/') ||
      // Add support for coordinate-based URLs
      url.includes('google.com/maps?lat=') ||
      url.includes('google.com/maps?q=')
    );
  };

  const isValidExternalLink = (url) => {
    return url && typeof url === 'string' && (
      url.startsWith('http://') ||
      url.startsWith('https://') ||
      url.startsWith('www.')
    );
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const handleCloseEventModal = () => {
    setShowEventModal(false);
    setSelectedEvent(null);
  };

  // Carousel navigation functions
  const nextCarouselSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCarouselDirection('next');
    setTimeout(() => {
      setCurrentCarouselIndex((prevIndex) =>
        prevIndex === carouselPictures.length - 1 ? 0 : prevIndex + 1
      );
      setIsTransitioning(false);
    }, 800);
  };

  const prevCarouselSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCarouselDirection('prev');
    setTimeout(() => {
      setCurrentCarouselIndex((prevIndex) =>
        prevIndex === 0 ? carouselPictures.length - 1 : prevIndex - 1
      );
      setIsTransitioning(false);
    }, 800);
  };

  const goToCarouselSlide = (index) => {
    if (isTransitioning || index === currentCarouselIndex) return;
    setIsTransitioning(true);
    setCarouselDirection(index > currentCarouselIndex ? 'next' : 'prev');
    setTimeout(() => {
      setCurrentCarouselIndex(index);
      setIsTransitioning(false);
    }, 800);
  };

  // Auto-transition effect
  useEffect(() => {
    if (carouselPictures.length <= 1 || carouselPaused || isTransitioning) return;

    const interval = setInterval(() => {
      setIsTransitioning(true);
      setCarouselDirection('next');
      setTimeout(() => {
        setCurrentCarouselIndex((prevIndex) =>
          prevIndex === carouselPictures.length - 1 ? 0 : prevIndex + 1
        );
        setIsTransitioning(false);
      }, 800);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [carouselPictures.length, currentCarouselIndex, carouselPaused, isTransitioning]);

  // Social Media Icons Component
  const SocialMediaIcons = () => {
    const icons = [];

    if (isValidInstagramId(club.instagram_id)) {
      icons.push(
        <Tooltip key="instagram" title="Instagram">
          <IconButton
            onClick={() => window.open(`https://instagram.com/${club.instagram_id}`, '_blank')}
            sx={{
              color: 'text.secondary',
              '&:hover': { color: '#E4405F' }
            }}
          >
            <Instagram />
          </IconButton>
        </Tooltip>
      );
    }

    if (isValidFacebookUrl(club.facebook_url)) {
      icons.push(
        <Tooltip key="facebook" title="Facebook">
          <IconButton
            onClick={() => window.open(club.facebook_url, '_blank')}
            sx={{
              color: 'text.secondary',
              '&:hover': { color: '#1877F2' }
            }}
          >
            <Facebook />
          </IconButton>
        </Tooltip>
      );
    }

    if (isValidYouTubeUrl(club.youtube_url)) {
      icons.push(
        <Tooltip key="youtube" title="YouTube">
          <IconButton
            onClick={() => window.open(club.youtube_url, '_blank')}
            sx={{
              color: 'text.secondary',
              '&:hover': { color: '#FF0000' }
            }}
          >
            <YouTube />
          </IconButton>
        </Tooltip>
      );
    }

    if (isValidMapsUrl(club.maps_url)) {
      icons.push(
        <Tooltip key="maps" title="Location">
          <IconButton
            onClick={() => window.open(club.maps_url, '_blank')}
            sx={{
              color: 'text.secondary',
              '&:hover': { color: '#4285F4' }
            }}
          >
            <LocationOn />
          </IconButton>
        </Tooltip>
      );
    }

    return icons.length > 0 ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
        {icons}
      </Box>
    ) : null;
  };

  if (loading) return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 4 }, textAlign: "center" }}>
      <CircularProgress />
    </Box>
  );

  if (error) return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 4 }, textAlign: "center" }}>
      <Typography color="error">{error}</Typography>
    </Box>
  );

  if (!club) return null;

  return (
    <Box sx={{ 
      p: { xs: 0, sm: 1, md: 4 }, 
      maxWidth: 1200, 
      mx: 'auto',
      overflowX: "hidden",
      width: "100%"
    }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 1, sm: 2, md: 4 } }}>
        {/* Main Content (Left, 60%) */}
        <Box sx={{ 
          flex: 3, 
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Paper sx={{ p: { xs: 1, sm: 2, md: 4 }, mb: { xs: 2, sm: 3, md: 4 } }}>
            {/* Header with avatar, name, and follow button */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, mb: 3 }}>
              <Avatar
                src={club.avatar_url || defaultAvatar}
                alt={club.name}
                sx={{ 
                  width: 120, 
                  height: 120, 
                  fontSize: '3rem',
                  '& img': {
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '50%'
                  }
                }}
              >
                {club.name.charAt(0)}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" alignItems="center" gap={2} mb={1}>
                  <Typography variant="h3" fontWeight="bold" sx={{ color: '#e91e63' }}>
                    {club.name}
                  </Typography>
                  <MKButton
                    variant={isFollowing ? "outlined" : "contained"}
                    onClick={handleFollowToggle}
                    disabled={followLoading}
                    sx={{ 
                      minWidth: 100,
                      bgcolor: isFollowing ? 'transparent' : PRIMARY_PINK,
                      color: isFollowing ? PRIMARY_PINK : '#ffffff',
                      borderColor: PRIMARY_PINK,
                      '&:hover': {
                        bgcolor: isFollowing ? 'rgba(233, 30, 99, 0.04)' : PRIMARY_PINK_DARK
                      }
                    }}
                  >
                    {followLoading ? (
                      <CircularProgress size={20} />
                    ) : isFollowing ? (
                      "Unfollow"
                    ) : (
                      "Follow"
                    )}
                  </MKButton>
                </Stack>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" color="text.secondary">
                    @{club.handle}
                  </Typography>
                  <SocialMediaIcons />
                </Box>
                <Typography variant="body1" color="text.secondary">
                  {club.follower_count || 0} followers
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ my: 3 }} />
            {/* Description */}
            <Box>
              <Typography variant="h5" fontWeight="bold" mb={2} sx={{ color: '#e91e63' }}>
                About
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  lineHeight: 1.6,
                  whiteSpace: 'pre-line'
                }}
              >
                {club.description || "No description available."}
              </Typography>
              {/* External Link - Display above About section */}
              {isValidExternalLink(club.external_link) && (
                <Box sx={{ mt: 3, mb: 2 }}>
                  <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                    <a
                      href={club.external_link.startsWith('http://') || club.external_link.startsWith('https://') ? club.external_link : `https://${club.external_link}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: '#e91e63',
                        textDecoration: 'none',
                        fontWeight: 500
                      }}
                    >
                      {club.external_link}
                    </a>
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
          {/* Discord Join Button */}
          {isValidDiscordUrl(club.discord_url) && (
            <Paper sx={{ p: 3, mb: { xs: 2, sm: 3, md: 4 }, textAlign: 'center' }}>
              <Typography variant="h6" fontWeight="bold" mb={2} sx={{ color: '#e91e63' }}>
                Join Our Discord Community
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Connect with other members, get updates, and join the conversation!
              </Typography>
              <MKButton
                variant="contained"
                onClick={() => window.open(club.discord_url, '_blank')}
                sx={{
                  bgcolor: '#5865F2',
                  color: '#ffffff',
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 4,
                  py: 1.5,
                  '&:hover': {
                    bgcolor: '#4752C4'
                  }
                }}
              >
                Join Discord Server
              </MKButton>
            </Paper>
          )}


          {/* Carousel Section */}
          {club?.show_carousel && carouselPictures.length > 0 && (
            <Paper sx={{ p: { xs: 1, sm: 2, md: 4 }, mb: { xs: 2, sm: 3, md: 4 } }}>
              <Typography variant="h5" fontWeight="bold" mb={3} sx={{ color: '#e91e63' }}>
                Gallery
              </Typography>
              <Box
                sx={{ position: 'relative', width: '100%', height: 400 }}
                onMouseEnter={() => setCarouselPaused(true)}
                onMouseLeave={() => setCarouselPaused(false)}
              >
                {/* Main Carousel Image */}
                <Box sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  borderRadius: 2,
                  overflow: 'hidden'
                }} className="carousel-container">
                  {/* Current Image */}
                  <img
                    key={`current-${currentCarouselIndex}`}
                    src={carouselPictures[currentCarouselIndex]?.image_url}
                    alt={carouselPictures[currentCarouselIndex]?.caption || 'Club picture'}
                    className={`carousel-image ${isTransitioning ? (carouselDirection === 'next' ? 'sliding-next' : 'sliding-prev') : 'current'}`}
                    style={{
                      cursor: 'pointer'
                    }}
                    onClick={() => window.open(carouselPictures[currentCarouselIndex]?.image_url, '_blank')}
                  />

                  {/* Next Image (for next transitions) */}
                  {carouselDirection === 'next' && (
                    <img
                      key={`next-${currentCarouselIndex}`}
                      src={carouselPictures[(currentCarouselIndex + 1) % carouselPictures.length]?.image_url}
                      alt={carouselPictures[(currentCarouselIndex + 1) % carouselPictures.length]?.caption || 'Club picture'}
                      className={`carousel-image ${isTransitioning ? 'sliding-in-next' : 'next'}`}
                      style={{
                        cursor: 'pointer'
                      }}
                      onClick={() => window.open(carouselPictures[(currentCarouselIndex + 1) % carouselPictures.length]?.image_url, '_blank')}
                    />
                  )}

                  {/* Previous Image (for prev transitions) */}
                  {carouselDirection === 'prev' && (
                    <img
                      key={`prev-${currentCarouselIndex}`}
                      src={carouselPictures[currentCarouselIndex === 0 ? carouselPictures.length - 1 : currentCarouselIndex - 1]?.image_url}
                      alt={carouselPictures[currentCarouselIndex === 0 ? carouselPictures.length - 1 : currentCarouselIndex - 1]?.caption || 'Club picture'}
                      className={`carousel-image ${isTransitioning ? 'sliding-in-prev' : 'prev'}`}
                      style={{
                        cursor: 'pointer'
                      }}
                      onClick={() => window.open(carouselPictures[currentCarouselIndex === 0 ? carouselPictures.length - 1 : currentCarouselIndex - 1]?.image_url, '_blank')}
                    />
                  )}

                  {/* Navigation Arrows */}
                  {carouselPictures.length > 1 && (
                    <>
                      <IconButton
                        onClick={prevCarouselSlide}
                        sx={{
                          position: 'absolute',
                          left: 16,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.7)'
                          }
                        }}
                      >
                        <ChevronLeft />
                      </IconButton>
                      <IconButton
                        onClick={nextCarouselSlide}
                        sx={{
                          position: 'absolute',
                          right: 16,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.7)'
                          }
                        }}
                      >
                        <ChevronRight />
                      </IconButton>

                      {/* Pause Indicator */}
                      {carouselPaused && (
                        <Box sx={{
                          position: 'absolute',
                          top: 16,
                          right: 16,
                          backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: 1,
                          fontSize: '0.75rem',
                          fontWeight: 500
                        }}>
                          PAUSED
                        </Box>
                      )}
                    </>
                  )}

                  {/* Caption Overlay */}
                  {carouselPictures[currentCarouselIndex]?.caption && (
                    <Box sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.7))',
                      color: 'white',
                      padding: 2,
                      textAlign: 'center'
                    }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {carouselPictures[currentCarouselIndex].caption}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Carousel Indicators */}
                {carouselPictures.length > 1 && (
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 1,
                    mt: 2
                  }}>
                    {carouselPictures.map((_, index) => (
                      <Box
                        key={index}
                        onClick={() => goToCarouselSlide(index)}
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: index === currentCarouselIndex ? '#e91e63' : '#ccc',
                          cursor: 'pointer',
                          transition: 'background-color 0.3s ease',
                          '&:hover': {
                            backgroundColor: index === currentCarouselIndex ? '#c2185b' : '#999'
                          }
                        }}
                      />
                    ))}
                  </Box>
                )}

                {/* Image Counter */}
                {carouselPictures.length > 1 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      textAlign: 'center',
                      mt: 1,
                      color: theme.palette.mode === 'dark' ? 'white' : 'black'
                    }}
                  >
                    {currentCarouselIndex + 1} of {carouselPictures.length}
                  </Typography>
                )}
              </Box>
            </Paper>
          )}
          
          {/* News Section - Mobile Only */}
          <Paper sx={{ 
            p: { xs: 1, sm: 2, md: 4 }, 
            mb: { xs: 2, sm: 3, md: 4 },
            display: { xs: 'block', md: 'none' }, // Show only on mobile
            order: { xs: 1, md: 0 } // Show above feed on mobile
          }}>
            <Typography variant="h5" fontWeight="bold" mb={2} sx={{ color: '#e91e63' }}>
              News
            </Typography>
            {newsLoading ? (
              <CircularProgress size={24} />
            ) : news.length === 0 ? (
              <Typography variant="body2" sx={{ color: theme.palette.mode === 'dark' ? 'white' : 'black' }}>
                No news yet.
              </Typography>
            ) : (
              <Box>
                {news.map(item => (
                  <Box key={item.id} sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {item.content ? item.content.slice(0, 80) + (item.content.length > 80 ? '...' : '') : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(item.posted_at)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>

          {/* Upcoming Events Section - Mobile Only */}
          <Paper sx={{ 
            p: { xs: 1, sm: 2, md: 4 }, 
            mb: { xs: 2, sm: 3, md: 4 },
            display: { xs: 'block', md: 'none' }, // Show only on mobile
            order: { xs: 2, md: 0 } // Show above feed on mobile
          }}>
            <Typography variant="h5" fontWeight="bold" mb={2} sx={{ color: '#e91e63' }}>
              Upcoming Events
            </Typography>
            {eventsLoading ? (
              <CircularProgress size={24} />
            ) : events.length === 0 ? (
              <Typography variant="body2" sx={{ color: theme.palette.mode === 'dark' ? 'white' : 'black' }}>
                No upcoming events.
              </Typography>
            ) : (
              <Box>
                {events.map(event => (
                  <Box 
                    key={event.id} 
                    sx={{ 
                      mb: 2, 
                      p: 2, 
                      border: '1px solid #e0e0e0', 
                      borderRadius: 1,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark' ? '#333' : '#f0f0f0',
                      },
                    }}
                    onClick={() => handleEventClick(event)}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      {event.title}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        mb: 0.5,
                        whiteSpace: 'pre-line'
                      }}
                    >
                      {event.description ? event.description.slice(0, 80) + (event.description.length > 80 ? '...' : '') : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {event.start_time ? new Date(event.start_time).toLocaleString() : ''}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>

          {/* Feed Section */}
          <Paper sx={{ 
            p: { xs: 1, sm: 2, md: 4 }, 
            mb: { xs: 2, sm: 3, md: 4 },
            order: { xs: 3, md: 0 } // Show after news and events on mobile, normal order on desktop
          }}>
            <Typography variant="h5" fontWeight="bold" mb={2} sx={{ color: '#e91e63' }}>
              Club Feed
            </Typography>
            {club?.club_mod_feed && (
              <Typography variant="body2" color="text.secondary" mb={2} sx={{ fontStyle: 'italic' }}>
                🔒 Mod-only feed: Only club owners, admins, and moderators can post
              </Typography>
            )}
            {isLoggedIn && userCanPost && (
              <MKBox sx={{ mb: 3, position: 'relative', pb: 2 }}>
                <textarea
                  value={newPost}
                  onChange={e => setNewPost(e.target.value)}
                  rows={3}
                  style={{
                    width: '100%',
                    resize: 'vertical',
                    padding: 8,
                    fontSize: 16,
                    backgroundColor: theme.palette.mode === 'dark' ? '#333' : '#fff',
                    color: theme.palette.mode === 'dark' ? 'white' : 'black',
                    border: `1px solid ${theme.palette.mode === 'dark' ? '#555' : '#ccc'}`
                  }}
                  placeholder="Write something..."
                  disabled={posting}
                />
                <MKButton
                  variant="contained"
                  size="small"
                  sx={{ 
                    mt: 0, 
                    float: 'right', 
                    minWidth: 80,
                    bgcolor: PRIMARY_PINK,
                    color: '#ffffff',
                    '&:hover': {
                      bgcolor: PRIMARY_PINK_DARK
                    }
                  }}
                  onClick={handlePost}
                  disabled={posting || !newPost.trim()}
                >
                  {posting ? "Posting..." : "Post"}
                </MKButton>
              </MKBox>
            )}
            {isLoggedIn && !userCanPost && club?.club_mod_feed && (
              <MKBox sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  🔒 This is a mod-only feed. Only club owners, admins, and moderators can post.
                </Typography>
              </MKBox>
            )}
            {feedLoading ? (
              <CircularProgress size={24} />
            ) : feedError ? (
              <Typography color="error">{feedError}</Typography>
            ) : feed.length === 0 ? (
              <Typography variant="body2" sx={{ color: theme.palette.mode === 'dark' ? 'white' : 'black' }}>
                No posts yet.
              </Typography>
            ) : (
              <MKBox>
                {paginatedFeed.map(post => (
                  <MKBox
                    key={post.id}
                    sx={{
                      mb: { xs: 2, sm: 3, md: 4 },
                      p: 2,
                      border: '1px solid #eee',
                      borderRadius: 2,
                      pb: replyBoxes[post.id] !== undefined ? 3.25 : 2 // about 15px extra if reply box is open
                    }}
                  >
                    {/* Post Header with User Info */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Avatar
                        src={post.avatar_url}
                        sx={{ width: 32, height: 32, fontSize: '0.875rem' }}
                      >
                        {post.display_name?.charAt(0)?.toUpperCase() || 'U'}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="bold" sx={{ color: theme.palette.mode === 'dark' ? 'white' : 'black' }}>
                          {post.display_name || 'Unknown User'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {post.membership_status} • {formatDate(post.created_at)}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {post.content}
                    </Typography>

                    {userCanPost && (
                      <MKButton size="small" onClick={() => openReplyBox(post.id)} sx={{ mb: 1 }}>
                        Comment
                      </MKButton>
                    )}
                    {/* Replies */}
                    <MKBox sx={{ ml: 3 }}>
                      {post.replies && post.replies.map(reply => (
                        <MKBox key={reply.id} sx={{ mb: 1, p: 1, borderLeft: '2px solid #1976d2' }}>
                          {/* Reply Header with User Info */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Avatar
                              src={reply.avatar_url}
                              sx={{ width: 24, height: 24, fontSize: '0.75rem' }}
                            >
                              {reply.display_name?.charAt(0)?.toUpperCase() || 'U'}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.875rem', color: theme.palette.mode === 'dark' ? 'white' : 'black' }}>
                                {reply.display_name || 'Unknown User'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                {reply.membership_status} • {formatDate(reply.created_at)}
                              </Typography>
                            </Box>
                          </Box>

                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            {reply.content}
                          </Typography>
                        </MKBox>
                      ))}
                      {/* Reply box */}
                      {replyBoxes[post.id] !== undefined && userCanPost && (
                        <MKBox sx={{ mt: 1, pb: 2 }}>
                          <textarea
                            value={replyBoxes[post.id]}
                            onChange={e => handleReplyChange(post.id, e.target.value)}
                            rows={2}
                            style={{
                              width: '100%',
                              resize: 'vertical',
                              padding: 6,
                              fontSize: 15,
                              backgroundColor: theme.palette.mode === 'dark' ? '#333' : '#fff',
                              color: theme.palette.mode === 'dark' ? 'white' : 'black',
                              border: `1px solid ${theme.palette.mode === 'dark' ? '#555' : '#ccc'}`
                            }}
                            placeholder="Write a reply..."
                            disabled={replying[post.id]}
                          />
                          <MKButton
                            variant="contained"
                            size="small"
                            sx={{ 
                              mt: 0, 
                              float: 'right', 
                              minWidth: 80,
                              bgcolor: PRIMARY_PINK,
                              color: '#ffffff',
                              '&:hover': {
                                bgcolor: PRIMARY_PINK_DARK
                              }
                            }}
                            onClick={() => handleReply(post.id)}
                            disabled={replying[post.id] || !replyBoxes[post.id]?.trim()}
                          >
                            {replying[post.id] ? "Replying..." : "Send"}
                          </MKButton>
                        </MKBox>
                      )}
                    </MKBox>
                  </MKBox>
                ))}
                
                {/* Pagination Controls */}
                {totalFeedPages > 1 && (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    mt: 3,
                    pt: 2,
                    borderTop: '1px solid #eee'
                  }}>
                    <Pagination
                      count={totalFeedPages}
                      page={feedPage}
                      onChange={handleFeedPageChange}
                      color="primary"
                      size={isMobile ? "small" : "medium"}
                      sx={{
                        '& .MuiPaginationItem-root': {
                          color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000',
                        },
                        '& .MuiPaginationItem-page.Mui-selected': {
                          backgroundColor: PRIMARY_PINK,
                          color: '#ffffff',
                          '&:hover': {
                            backgroundColor: PRIMARY_PINK_DARK,
                          }
                        }
                      }}
                    />
                  </Box>
                )}
              </MKBox>
            )}
          </Paper>

          {/* Email Subscription Toggle */}
          {isFollowing && isLoggedIn && (
            <Paper sx={{ p: 3, mb: { xs: 2, sm: 3, md: 4 }, textAlign: 'center' }}>
              <Typography variant="h6" fontWeight="bold" mb={2} sx={{ color: '#e91e63' }}>
                Email Notifications
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                {emailSubscribed
                  ? "You're currently receiving email updates from this club."
                  : "You're not receiving email updates from this club."
                }
              </Typography>
              <MKButton
                variant="outlined"
                onClick={handleEmailSubscriptionToggle}
                disabled={emailSubLoading}
                sx={{
                  textTransform: 'none',
                  borderColor: emailSubscribed ? '#f44336' : '#4caf50',
                  color: emailSubscribed ? '#f44336' : '#4caf50',
                  '&:hover': {
                    borderColor: emailSubscribed ? '#d32f2f' : '#388e3c',
                    backgroundColor: emailSubscribed ? 'rgba(244, 67, 54, 0.04)' : 'rgba(76, 175, 80, 0.04)'
                  }
                }}
              >
                {emailSubLoading ? (
                  <CircularProgress size={20} />
                ) : emailSubscribed ? (
                  "Disable Email Notifications"
                ) : (
                  "Enable Email Notifications"
                )}
              </MKButton>
            </Paper>
          )}
        </Box>
        {/* Sidebar (Right, 40%) */}
        <Box sx={{ flex: 2, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {/* News Section - Desktop Only */}
          <Paper sx={{ 
            p: { xs: 1, sm: 2, md: 4 },
            display: { xs: 'none', md: 'block' } // Show only on desktop
          }}>
            <Typography variant="h5" fontWeight="bold" mb={2} sx={{ color: '#e91e63' }}>
              News
            </Typography>
            {newsLoading ? (
              <CircularProgress size={24} />
            ) : news.length === 0 ? (
              <Typography variant="body2" sx={{ color: theme.palette.mode === 'dark' ? 'white' : 'black' }}>
                No news yet.
              </Typography>
            ) : (
              <Box>
                {news.map(item => (
                  <Box key={item.id} sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {item.content ? item.content.slice(0, 80) + (item.content.length > 80 ? '...' : '') : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(item.posted_at)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
          {/* Upcoming Events Section - Desktop Only */}
          <Paper sx={{ 
            p: { xs: 1, sm: 2, md: 4 }, 
            mt: 4,
            display: { xs: 'none', md: 'block' } // Show only on desktop
          }}>
            <Typography variant="h5" fontWeight="bold" mb={2} sx={{ color: '#e91e63' }}>
              Upcoming Events
            </Typography>
            {eventsLoading ? (
              <CircularProgress size={24} />
            ) : events.length === 0 ? (
              <Typography variant="body2" sx={{ color: theme.palette.mode === 'dark' ? 'white' : 'black' }}>
                No upcoming events.
              </Typography>
            ) : (
              <Box>
                {events.map(event => (
                  <Box
                    key={event.id}
                    sx={{
                      mb: 2,
                      p: 2,
                      border: '1px solid #eee',
                      borderRadius: 2,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#f0f0f0',
                      },
                    }}
                    onClick={() => handleEventClick(event)}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      {event.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {event.start_time ? new Date(event.start_time).toLocaleString() : ''}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Box>
      </Box>



      <EventDetailsModal
        open={showEventModal}
        onClose={handleCloseEventModal}
        event={selectedEvent}
        clubName={club?.name || ""}
      />

      <SignInModal
        open={showSignInModal}
        onClose={() => setShowSignInModal(false)}
        onSignIn={handleSignIn}
        onSwitchToSignUp={() => setShowSignInModal(false)}
        onSwitchToForgotPassword={() => setShowSignInModal(false)}
      />
    </Box>
  );
}

export default ClubLandingPage; 